import React from 'react'
import { Modal, Form, Input, Select, Space, DatePicker, Tag, theme } from 'antd'
import { TASK_TYPE_CONFIG } from '../config/taskTypes'
import { STAFF_MEMBERS, CURRENT_USER, DATE_FORMAT } from '../config/constants'
import dayjs from 'dayjs'
import styles from '../styles/CreateTaskModal.module.css'
import { useSelector } from 'react-redux'
import { hexToRGBA } from '../../../../../../lib/utils'
import { FlagOutlined } from '@ant-design/icons'

const { Option } = Select

const EditTaskModal = ({ visible, onCancel, onSubmit, form, medicalProfs }) => {
  const authState = useSelector((state) => state.auth)
  const { token } = theme.useToken()

  const getRole = (staff) => {
    switch (staff.extraData.role.type) {
      case 'medical_professional':
        if (staff.extraData.profession === 'nurse') {
          return 'Nurse'
        } else if (staff.extraData.profession === 'doctor') {
          return 'Doctor'
        }
        break
      case 'reception':
        return 'Receptionist'
      case 'admin':
        return 'Admin'
      case 'super_admin':
        return 'Super Admin'
      default:
        return 'Unknown'
    }
  }

  // Define inline styles for the component
  const modalTitleStyle = {
    color: '#1890ff',
    fontWeight: 600,
    fontSize: '20px',
    padding: '4px 0'
  }

  const modalHeaderStyle = {
    padding: '16px 24px'
  }

  const saveButtonStyle = {
    background: 'rgba(24, 144, 255, 0.15)',
    borderColor: '#1890ff',
    color: '#1890ff',
    fontWeight: 'normal'
  }

  const cancelButtonStyle = {
    background: 'rgba(245, 67, 54, 0.15)',
    borderColor: '#f44336',
    color: '#d32f2f',
    fontWeight: 'normal'
  }

  const tagStyle = {
    display: 'flex',
    alignItems: 'center',
    marginRight: '8px',
    padding: '4px 8px',
    fontSize: '14px',
    lineHeight: '22px',
    height: 'auto',
    color: 'white',
    fontWeight: 'bold'
  }

  const iconStyle = {
    marginRight: '8px',
    fontSize: '16px',
    color: 'white'
  }

  const descriptionStyle = {
    fontSize: '14px'
  }

  const modalBodyStyle = {
    padding: '20px 24px'
  }

  return (
    <Modal
      title={<div style={modalTitleStyle}>Edit Task</div>}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      className={styles.taskModal}
      width={600}
      bodyStyle={modalBodyStyle}
      headerStyle={modalHeaderStyle}
      okText='Save Changes'
      okButtonProps={{
        style: saveButtonStyle
      }}
      cancelButtonProps={{
        style: cancelButtonStyle
      }}
    >
      <Form
        form={form}
        layout='vertical'
        onFinish={(values) => {
          const formattedValues = {
            ...values,
            dueDate:
              values.dueDate && typeof values.dueDate.toISOString === 'function'
                ? values.dueDate.toISOString()
                : values.dueDate
          }
          onSubmit(formattedValues)
        }}
        style={{ marginTop: '12px' }}
      >
        <Form.Item
          name='description'
          label='Task Description'
          rules={[{ required: true, message: 'Please enter task description' }]}
          style={{ marginBottom: '24px' }}
        >
          <Input size='large' />
        </Form.Item>
        <Form.Item
          name='taskType'
          label='Task Type'
          rules={[{ required: true, message: 'Please select task type' }]}
          style={{ marginBottom: '24px' }}
        >
          <Select size='large'>
            {Object.entries(TASK_TYPE_CONFIG).map(([key, config]) => (
              <Option key={key} value={key}>
                <Space>
                  <Tag color={config.color} style={tagStyle}>
                    <span style={iconStyle}>{config.icon}</span>
                    {config.label}
                  </Tag>
                  <span className={styles.typeDescription} style={descriptionStyle}>
                    {config.description}
                  </span>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name='assignee'
          label='Assignee'
          rules={[{ required: true, message: 'Please select who is responsible for this task' }]}
          style={{ marginBottom: '24px' }}
        >
          <Select size='large' showSearch placeholder='Select staff member' optionFilterProp='children'>
            <Option key={authState.agiliteUser?._id} value={authState.agiliteUser?._id}>
              You (Self-assigned)
            </Option>
            {medicalProfs
              .filter((staff) => staff._id !== authState.agiliteUser?._id)
              .map((staff) => (
                <Option key={staff._id} value={staff.name}>
                  {staff.firstName} {staff.lastName} ({getRole(staff)})
                </Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item
          name='priority'
          label='Priority'
          rules={[{ required: true, message: 'Please select priority' }]}
          style={{ marginBottom: '24px' }}
        >
          <Select size='large'>
            <Option value='high'>
              <Space>
                <Tag color='#f5222d' style={{ ...tagStyle, marginRight: 0 }}>
                  <FlagOutlined style={iconStyle} /> High
                </Tag>
              </Space>
            </Option>
            <Option value='medium'>
              <Space>
                <Tag color='#faad14' style={{ ...tagStyle, marginRight: 0 }}>
                  <FlagOutlined style={iconStyle} /> Medium
                </Tag>
              </Space>
            </Option>
            <Option value='low'>
              <Space>
                <Tag color='#52c41a' style={{ ...tagStyle, marginRight: 0 }}>
                  <FlagOutlined style={iconStyle} /> Low
                </Tag>
              </Space>
            </Option>
          </Select>
        </Form.Item>
        <Form.Item
          name='dueDate'
          label='Due Date'
          rules={[{ required: true, message: 'Please select due date' }]}
          style={{ marginBottom: '12px' }}
        >
          <DatePicker
            size='large'
            format={DATE_FORMAT}
            className={styles.datePicker}
            disabledDate={(current) => {
              return current && current < dayjs().startOf('day')
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default EditTaskModal
